import {
  resolveClinicalBookingChromeKey,
  type ClinicalBookingChromeKey,
} from '@/components/consultation/clinicalBookingStepChrome'
import { CustomerPages } from '@/router/customerRoutes'
import { SignupPages } from '@/router/signupRoutes'
import {
  resolveClinicalProductStep,
  type ClinicalBillingPath,
  type ClinicalBookingStep,
  type ClinicalDraftSubPhase,
  type ClinicalProductStep,
  type ClinicalReservedSubPhase,
} from '@/stores/clinicalConsultationBookingFlow'

export const CONSULTATION_BOOKING_ROUTE_PREFIX = '/consultation-booking'

const CONSULTATION_BOOKING_ROUTE_SEGMENT_BY_CHROME_KEY: Record<ClinicalBookingChromeKey, string> = {
  intro: 'intro',
  existing_consultation_gate: 'existing-consultation',
  appointment_cancelled: 'appointment-cancelled',
  reservation_expired: 'reservation-expired',
  unsupported_state: 'unsupported-state',
  profile: 'profile',
  insurance_category: 'insurance-category',
  cash_pay_eligible: 'cash-pay-eligible',
  insurance_on_file: 'insurance-on-file',
  insurance_details: 'insurance-details',
  insurance_verified: 'insurance-verified',
  slot_picker: 'slot-selection',
  hard_verification: 'hard-verification',
  ensuring_bridge_service: 'preparing-visit',
  payment_pending: 'payment',
  pending_settlement_payment: 'settlement-payment',
  provisioning: 'provisioning',
  scheduled: 'confirmed',
  loading: 'loading',
}

export const CONSULTATION_BOOKING_SIGNUP_ROUTE_BY_CHROME_KEY: Record<
  ClinicalBookingChromeKey,
  string
> = Object.fromEntries(
  Object.entries(CONSULTATION_BOOKING_ROUTE_SEGMENT_BY_CHROME_KEY).map(([key, segment]) => [
    key,
    `${CONSULTATION_BOOKING_ROUTE_PREFIX}/${segment}`,
  ]),
) as Record<ClinicalBookingChromeKey, string>

const CONSULTATION_BOOKING_CHROME_KEY_BY_ROUTE_SEGMENT = Object.fromEntries(
  Object.entries(CONSULTATION_BOOKING_ROUTE_SEGMENT_BY_CHROME_KEY).map(([key, segment]) => [
    segment,
    key as ClinicalBookingChromeKey,
  ]),
) as Record<string, ClinicalBookingChromeKey>

export function isConsultationBookingSignupPath(path: string): boolean {
  return (
    path === CONSULTATION_BOOKING_ROUTE_PREFIX ||
    path.startsWith(`${CONSULTATION_BOOKING_ROUTE_PREFIX}/`)
  )
}

export function resolveConsultationBookingSignupPath(chromeKey: ClinicalBookingChromeKey): string {
  return CONSULTATION_BOOKING_SIGNUP_ROUTE_BY_CHROME_KEY[chromeKey]
}

export function resolveConsultationBookingChromeKeyFromRoute(
  pathSegment: string,
): ClinicalBookingChromeKey | null {
  return CONSULTATION_BOOKING_CHROME_KEY_BY_ROUTE_SEGMENT[pathSegment] ?? null
}

export function resolveConsultationBookingRouteSegmentFromPath(path: string): string | null {
  if (!isConsultationBookingSignupPath(path)) {
    return null
  }
  if (path === CONSULTATION_BOOKING_ROUTE_PREFIX) {
    return null
  }
  return path.slice(CONSULTATION_BOOKING_ROUTE_PREFIX.length + 1)
}

export function resolveConsultationBookingRouteSegmentFromChromeKey(
  chromeKey: ClinicalBookingChromeKey,
): string {
  return CONSULTATION_BOOKING_ROUTE_SEGMENT_BY_CHROME_KEY[chromeKey]
}

export function resolveInitialConsultationBookingSignupPath(): string {
  return resolveConsultationBookingSignupPath('intro')
}

const CHROME_KEY_TO_PRODUCT_PREFLOW_STEP: Partial<
  Record<ClinicalBookingChromeKey, ClinicalProductStep>
> = {
  intro: 'intro',
  existing_consultation_gate: 'existing_consultation_gate',
  appointment_cancelled: 'appointment_cancelled',
  unsupported_state: 'unsupported_state',
  profile: 'profile',
  insurance_category: 'insurance_category',
  cash_pay_eligible: 'cash_pay_eligible',
  insurance_on_file: 'insurance_on_file',
  insurance_details: 'insurance_details',
  insurance_verified: 'insurance_verified',
}

/** Maps customer preflow route chrome to product step for reload hydration (no consultation id yet). */
export function resolveClinicalProductStepFromChromeKey(
  chromeKey: ClinicalBookingChromeKey | null,
): ClinicalProductStep | null {
  if (!chromeKey) {
    return null
  }
  return CHROME_KEY_TO_PRODUCT_PREFLOW_STEP[chromeKey] ?? null
}

export type ResolveCanonicalConsultationBookingSignupPathArgs = {
  hasActiveBookingSession: boolean
  isResumingConsultation: boolean
  isAvailabilityLoading: boolean
  showProductPreflow: boolean
  activeProductStep: ClinicalProductStep | null
  currentStep: ClinicalBookingStep
  draftSubPhase: ClinicalDraftSubPhase
  reservedSubPhase: ClinicalReservedSubPhase
  isContinuingBooking: boolean
  entrySource: 'account' | 'signup'
  billingPath: ClinicalBillingPath | null
  hasScheduledConsultation: boolean
  inPostPaymentConfirmationSession: boolean
}

export function resolveCanonicalConsultationBookingSignupPathFromStore(
  args: ResolveCanonicalConsultationBookingSignupPathArgs,
): string {
  if (!args.hasActiveBookingSession) {
    if (args.isResumingConsultation) {
      return resolveConsultationBookingSignupPath('loading')
    }
    return resolveInitialConsultationBookingSignupPath()
  }

  const chromeKey = resolveClinicalBookingChromeKey({
    isResumingConsultation: args.isResumingConsultation,
    isAvailabilityLoading: args.isAvailabilityLoading,
    showProductPreflow: args.showProductPreflow,
    activeProductStep: args.activeProductStep,
    currentStep: args.currentStep,
    draftSubPhase: args.draftSubPhase,
    reservedSubPhase: args.reservedSubPhase,
    billingPath: args.billingPath,
    isContinuingBooking: args.isContinuingBooking,
    hasScheduledConsultation: args.hasScheduledConsultation,
    inPostPaymentConfirmationSession: args.inPostPaymentConfirmationSession,
  })

  const resolvedChromeKey =
    chromeKey === 'ensuring_bridge_service' && args.billingPath === 'insured'
      ? 'hard_verification'
      : chromeKey

  return resolveConsultationBookingSignupPath(resolvedChromeKey)
}

const SIGNUP_BOOKING_KEY = 'wantsBookingDuringSignup'

export type ClinicalBookingHistoryEntrySource = 'account' | 'signup' | 'consultations_hub'

export type ClinicalBookingEntrySourceResolutionArgs = {
  historyStateEntrySource: unknown
  signupCompleted: boolean
}

export function isClinicalBookingHistoryEntrySource(
  value: unknown,
): value is ClinicalBookingHistoryEntrySource {
  return value === 'account' || value === 'signup' || value === 'consultations_hub'
}

export function resolveBookingEntrySource(
  args: ClinicalBookingEntrySourceResolutionArgs,
): 'account' | 'signup' {
  if (args.historyStateEntrySource === 'signup') {
    return 'signup'
  }
  if (
    args.historyStateEntrySource === 'account' ||
    args.historyStateEntrySource === 'consultations_hub'
  ) {
    return 'account'
  }
  if (sessionStorage.getItem(SIGNUP_BOOKING_KEY) === 'true') {
    return 'signup'
  }
  return args.signupCompleted ? 'account' : 'signup'
}

export function resolveClinicalBookingExitPath(historyStateEntrySource: unknown): string {
  if (historyStateEntrySource === 'consultations_hub') {
    return CustomerPages.Consultations.path
  }
  return CustomerPages.You.path
}

export function resolveClinicalBookingCancelExitPath(args: {
  historyStateEntrySource: unknown
  signupCompleted: boolean
}): string {
  if (args.historyStateEntrySource === 'consultations_hub') {
    return CustomerPages.Consultations.path
  }
  if (args.historyStateEntrySource === 'account') {
    return CustomerPages.You.path
  }
  return args.signupCompleted ? CustomerPages.Home.path : SignupPages.PlanSelection.path
}

export function resolveCanonicalConsultationBookingSignupPathFromProductState(args: {
  productStep: ClinicalProductStep | null
  productPreflowComplete: boolean
  hasScheduledConsultation: boolean
  currentStep: ClinicalBookingStep
  draftSubPhase: ClinicalDraftSubPhase
  reservedSubPhase: ClinicalReservedSubPhase
  billingPath: ClinicalBillingPath | null
  isContinuingBooking: boolean
  isResumingConsultation: boolean
  isAvailabilityLoading: boolean
  hasActiveBookingSession: boolean
  entrySource: 'account' | 'signup'
  inPostPaymentConfirmationSession: boolean
}): string {
  const activeProductStep = resolveClinicalProductStep({
    productStep: args.productStep,
    productPreflowComplete: args.productPreflowComplete,
    hasScheduledConsultation: args.hasScheduledConsultation,
    currentStep: args.currentStep,
    draftSubPhase: args.draftSubPhase,
    reservedSubPhase: args.reservedSubPhase,
    billingPath: args.billingPath,
    isResume: args.isContinuingBooking,
    inPostPaymentConfirmationSession: args.inPostPaymentConfirmationSession,
  })

  const showProductPreflow = activeProductStep != null && !args.productPreflowComplete

  return resolveCanonicalConsultationBookingSignupPathFromStore({
    hasActiveBookingSession: args.hasActiveBookingSession,
    isResumingConsultation: args.isResumingConsultation,
    isAvailabilityLoading: args.isAvailabilityLoading,
    showProductPreflow,
    activeProductStep,
    currentStep: args.currentStep,
    draftSubPhase: args.draftSubPhase,
    reservedSubPhase: args.reservedSubPhase,
    isContinuingBooking: args.isContinuingBooking,
    entrySource: args.entrySource,
    billingPath: args.billingPath,
    hasScheduledConsultation: args.hasScheduledConsultation,
    inPostPaymentConfirmationSession: args.inPostPaymentConfirmationSession,
  })
}
